/* eslint-disable no-param-reassign */
// mapbox-gl-draw modes work by mutating the state and display features they
// are handed; that is the contract, not an accident.
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Feature } from 'geojson';
import { circle, distance, point } from '@turf/turf';
import * as dragPan from './dragPan';
import { ModeContext } from './dragPan';

const { constants: Constants, lib } = MapboxDraw;

type CircleEvent = MapboxDraw.MapMouseEvent | MapboxDraw.MapTouchEvent;

function startCircle(this: ModeContext, state: any, e: CircleEvent) {
  if (state.polygon.properties.center.length === 0) {
    state.polygon.properties.center = [e.lngLat.lng, e.lngLat.lat];
  }
}

function resizeCircle(this: ModeContext, state: any, e: CircleEvent) {
  const { center } = state.polygon.properties;
  if (center.length > 0) {
    const radiusInKm = distance(
      point(center),
      point([e.lngLat.lng, e.lngLat.lat]),
      { units: 'kilometers' }
    );
    state.polygon.incomingCoords(
      circle(center, radiusInKm).geometry.coordinates
    );
    state.polygon.properties.radiusInKm = radiusInKm;
  }
}

function finishCircle(this: ModeContext, state: any) {
  return this.changeMode(Constants.modes.SIMPLE_SELECT, {
    featureIds: [state.polygon.id]
  });
}

// A click or tap without a drag draws nothing.
function cancelCircle(this: ModeContext, state: any) {
  state.polygon.properties.center = [];
}

// Press, drag and release to draw a circle around the press point.
const DragCircleMode: MapboxDraw.DrawCustomMode = {
  ...MapboxDraw.modes.draw_polygon,

  onSetup() {
    const polygon = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: { isCircle: true, center: [] },
      geometry: { type: Constants.geojsonTypes.POLYGON, coordinates: [[]] }
    });
    this.addFeature(polygon);

    this.clearSelectedFeatures();
    lib.doubleClickZoom.disable(this);
    dragPan.disable(this);
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.activateUIButton(Constants.types.POLYGON);
    this.setActionableState({
      trash: true,
      combineFeatures: false,
      uncombineFeatures: false
    });

    return { polygon, currentVertexPosition: 0 };
  },

  onMouseDown: startCircle,
  onTouchStart: startCircle,
  onDrag: resizeCircle,
  onMouseMove: resizeCircle,
  onMouseUp: finishCircle,
  onTouchEnd: finishCircle,
  onClick: cancelCircle,
  onTap: cancelCircle,

  // Runs for a finished circle and for an abandoned one alike (Escape, or
  // another tool picked mid-draw). simple_select in draw 1.5 only restores
  // dragPan when it was enabled as the mode started, so it has to be back on
  // before the next mode sets up.
  onStop(state: any) {
    MapboxDraw.modes.draw_polygon.onStop?.call(this, state);
    dragPan.enable(this as ModeContext);
  },

  toDisplayFeatures(state: any, geojson: Feature, display) {
    const isActivePolygon = geojson.properties?.id === state.polygon.id;
    geojson.properties = {
      ...geojson.properties,
      active: isActivePolygon
        ? Constants.activeStates.ACTIVE
        : Constants.activeStates.INACTIVE
    };
    return display(geojson);
  }
};

export default DragCircleMode;
